import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStore } from 'react-admin';
import { parse, stringify } from 'query-string';
import isEqual from 'lodash/isEqual';
import pickBy from 'lodash/pickBy';

const validQueryParams = ['page', 'perPage', 'sort', 'order', 'filter', 'displayedFilters'];

interface SyncStoreToLocationOptions {
    storeKey: string;
    sort: { field: string; order: string };
    perPage: number;
    filterDefaultValues?: Record<string, any>;
}

const parseQueryFromLocation = (location: { search: string }) => {
    const query: any = pickBy(
        parse(location.search),
        (_v, k) => validQueryParams.includes(k)
    );
    if (query.filter && typeof query.filter === 'string') {
        try { query.filter = JSON.parse(query.filter); } catch { delete query.filter; }
    }
    if (query.displayedFilters && typeof query.displayedFilters === 'string') {
        try { query.displayedFilters = JSON.parse(query.displayedFilters); } catch { delete query.displayedFilters; }
    }
    return query;
};

/**
 * Syncs stored list params to the URL when the component loads with
 * stored filters that aren't reflected in the query string.
 *
 * Adapted from https://github.com/marmelab/react-admin/pull/11022
 */
export const useSyncStoreToLocation = ({
    storeKey,
    sort,
    perPage,
    filterDefaultValues,
}: SyncStoreToLocationOptions) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [params] = useStore(storeKey, {} as any);

    useEffect(() => {
        const queryFromLocation = parseQueryFromLocation(location);

        // Don't override if URL already has list params
        if (Object.keys(queryFromLocation).length > 0) {
            return;
        }

        // Nothing stored to sync
        if (!params || Object.keys(params).length === 0) {
            return;
        }

        const defaultParams = {
            filter: filterDefaultValues || {},
            page: 1,
            perPage,
            sort: sort.field,
            order: sort.order,
        };

        const { displayedFilters: _displayedFilters, ...storedWithoutDisplayed } = params;

        // Don't sync if stored params equal defaults (keep clean URLs)
        if (isEqual(storedWithoutDisplayed, defaultParams)) {
            return;
        }

        navigate(
            {
                search: `?${stringify({
                    ...params,
                    filter: JSON.stringify(params.filter || {}),
                    displayedFilters: JSON.stringify(params.displayedFilters || {}),
                })}`,
            },
            { replace: true }
        );
    }, [params, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
};
